import { 
  rowHasMappedKey,
  trimUpdateValue,
  checkDuplicateKeyInBulkUpdate,
  getIdByExactName,
} from './sharedImportHelpers';

const parseUpdateUserWageValue = (wage) => {
  const normalizedWage = String(wage).trim().replaceAll(/[$\s,]/g, '');
  if (!normalizedWage) {
    throw new Error('Wage is empty');
  }
  if (!/^\d+(\.\d+)?$/.test(normalizedWage)) {
    throw new Error('Wage must contain only digits with an optional decimal part');
  }
  const [, decimalPart = ''] = normalizedWage.split('.');
  if (decimalPart.length > 2) {
    throw new Error('Wage can only have up to 2 decimal places');
  }
  const value = Number(normalizedWage);
  if (value <= 0) {
    throw new Error('Wage must be a positive number');
  }
  return value;
};

const resolveUpdateUserUnionClassId = (row, context) => {
  const formattedUnion = trimUpdateValue(row.union);
  const formattedLocal = trimUpdateValue(row.local);
  const formattedClass = trimUpdateValue(row.unionClass);

  const notAllColumnsFilled = !formattedUnion || !formattedLocal || !formattedClass;
  if (notAllColumnsFilled) {
    throw new Error("Must map all union, local, and class columns to update the user union");
  }

  const union = context.unions?.find((u) => u.name === formattedUnion);
  if (!union) {
    throw new Error(`Union "${formattedUnion}" not found`);
  }

  const local = context.unionLocals?.find(
    (l) => l.name === formattedLocal && l.unionId === union.id,
  );
  if (!local) {
    throw new Error(`Local "${formattedLocal}" not found under union "${formattedUnion}"`);
  }

  const unionClass = context.unionClasses?.find(
    (uc) => uc.name === formattedClass && uc.localId === local.id,
  );
  if (!unionClass?.id) {
    throw new Error(`Class "${formattedClass}" not found under local "${formattedLocal}"`);
  }

  return unionClass.id;
};

export const buildUpdateUserImportPayload = (row, context) => {
  const name = trimUpdateValue(row.name);
  if (!name) {
    throw new Error('Name is required');
  }

  const employeeId = rowHasMappedKey(row, 'employeeId') ? trimUpdateValue(row.employeeId) : null;

  let userId;
  if (employeeId) {
    const user = context.employeeIdMap?.[employeeId];
    if (!user) {
      throw new Error(`No user found with Employee ID "${employeeId}"`);
    }
    checkDuplicateKeyInBulkUpdate({
      key: employeeId,
      label: 'Employee ID',
      seen: context.bulkUserUpdateSeenEmployeeIds,
    });
    userId = user.id;
  } else {
    userId = getIdByExactName({
      name,
      map: context.userMap,
      errorMessage: `No user found with name "${name}"`,
    });
    checkDuplicateKeyInBulkUpdate({
      key: name,
      label: 'Name',
      seen: context.bulkUserUpdateSeenNames,
    });
  }

  const payload = {};

  const hasUnion = rowHasMappedKey(row, 'union');
  const hasLocal = rowHasMappedKey(row, 'local');
  const hasClass = rowHasMappedKey(row, 'unionClass');
  if (hasClass || hasLocal || hasUnion) {
    payload.classId = resolveUpdateUserUnionClassId(row, context);
  }

  if (rowHasMappedKey(row, 'position') && trimUpdateValue(row.position)) {
    const posName = trimUpdateValue(row.position);
    if (!context.roleMap?.[posName]) {
      throw new Error(`Role "${posName}" not found`);
    }
    payload.position = posName;
  }

  if (rowHasMappedKey(row, 'wage') && trimUpdateValue(row.wage) !== '') {
    payload.wage = parseUpdateUserWageValue(row.wage);
  }

  if (Object.keys(payload).length === 0) {
    throw new Error('No updatable fields in mapped columns');
  }

  return { updateId: userId, payload };
};
